import { memo, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { formatISODate } from "~/common/converters";
import { Button } from "../../Button";
import { IconButton } from "../../IconButton";
import { Icon } from "../../Icons";
import { Tag } from "../../Tag";
import { Tooltip } from "../../Tooltip";
import { Body } from "../../Typography/Body";
import { Heading } from "../../Typography/Heading";
import {
  CITATION_MARKER_ATTR,
  injectCitationMarkers,
  type SummaryCitationAnchor,
} from "../utils/citationMarkers";
import { remarkStripSoftBreaks } from "../utils/remarkStripSoftBreaks";
import { remarkUnderline } from "../utils/remarkUnderline";
import {
  getItemSearchMatches,
  highlightSearchTerm,
} from "../utils/searchHighlight";
import { CitationMarker } from "./CitationMarker";
import { HighlightedText } from "./HighlightedText";

// Hoisted: inline array literals would be new on every render, defeating both
// the memo below and react-markdown's own internal caching.
const REMARK_PLUGINS = [remarkGfm, remarkStripSoftBreaks, remarkUnderline];
const REHYPE_PLUGINS = [rehypeRaw];

interface MarkdownBodyProps {
  /** Markdown to render — already citation-injected and search-highlighted. */
  markdown: string;
  citationNumbers: ReadonlyMap<string, number>;
  onOpenCitation?: (citationRowId: string) => void;
  onRemoveCitation?: (citationRowId: string) => void;
}

/**
 * The entry body, split out and memoized because it is the single most
 * expensive thing a timeline row renders: react-markdown v10 has no internal
 * memo, so every render rebuilds the unified pipeline and re-parses the whole
 * string — including rehypeRaw, which serializes the tree to HTML and reparses
 * it through parse5. Measured 1.2-2.4ms per entry.
 *
 * react-window rebuilds its offset cache on every row measurement, so a single
 * row resizing re-renders every mounted row; without this the whole visible set
 * re-parsed its markdown each time, producing byte-identical output.
 *
 * Memoizing the PARENT instead would not work: it takes a `validationIssues`
 * element that is freshly created on every render, which would defeat the memo.
 * All of this component's props are value-comparable or already stable.
 */
const MarkdownBody = memo(function MarkdownBody({
  markdown,
  citationNumbers,
  onOpenCitation,
  onRemoveCitation,
}: MarkdownBodyProps) {
  const components = useMemo(
    () => ({
      p: ({ node: _node, ...props }: { node?: unknown }) => (
        <p className="indigo-timeline-item__markdown-content" {...props} />
      ),
      // `injectCitationMarkers` emits `<sup data-citation-ids="…">`, which
      // rehypeRaw passes through; this maps that one tag onto the interactive
      // marker and leaves every other `sup` alone.
      sup: ({
        node: _node,
        ...rest
      }: React.HTMLAttributes<HTMLElement> & { node?: unknown }) => {
        const ids = (rest as Record<string, unknown>)[CITATION_MARKER_ATTR];
        if (typeof ids !== "string" || !onOpenCitation || !onRemoveCitation) {
          return <sup {...rest} />;
        }
        return (
          <CitationMarker
            actions={ids.split(",").map((id) => ({
              citationRowId: id,
              displayNumber: citationNumbers.get(id) ?? 0,
            }))}
            onOpen={onOpenCitation}
            onRemove={onRemoveCitation}
          />
        );
      },
    }),
    [citationNumbers, onOpenCitation, onRemoveCitation],
  );

  return (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={REHYPE_PLUGINS}
      components={components}
    >
      {markdown}
    </ReactMarkdown>
  );
});

interface ContentV2Props {
  /** Date of the record (ISO string) */
  date: string | null;

  /** Title of the record */
  title: string;

  /** Doctor or provider name */
  doctorName: string;

  /** Markdown content to display */
  content: string;

  /** Source tag and page range combined (e.g. "AA P1-3") */
  sourceText: string;

  /** PDF file name shown as a tooltip on the sourceText */
  pdfFileName?: string;

  /** Row number (1-based) */
  rowNumber?: number;

  /** Source-appointment attribution for multi-appointment MRR views */
  entryInfo?: {
    appointmentId: string;
    isCurrent: boolean;
    appointmentDate: string | null;
    appointmentTypeLabel: string;
    href?: string;
  };

  /** Whether this item is expanded */
  isExpanded: boolean;

  /** Search query to highlight in content */
  searchQuery?: string;

  /** Index of the currently active match */
  activeMatchIndex?: number;

  /** Optional inline validation issues rendered between header and markdown body */
  validationIssues?: React.ReactNode;

  /**
   * Source citations anchored in this entry's summary. Empty (or omitted) when
   * the citation feature is off, in which case the content renders untouched.
   */
  citations?: readonly SummaryCitationAnchor[];

  /** Jump the document viewer to a citation's source block. */
  onOpenCitation?: (citationRowId: string) => void;

  /** Soft-delete one citation link. */
  onRemoveCitation?: (citationRowId: string) => void;

  /** Optional handler for the "File a ticket" button, rendered next to the
   *  row number in the footer. When omitted, the button is hidden. */
  onFileTicketClick?: () => void;

  /** Optional handler for the "View video" button, rendered between the
   *  markdown body and the source-tag footer. When omitted, the button is
   *  hidden — callers pass this only for sub-rosa video entries. */
  onViewVideoClick?: () => void;
}

/**
 * V2 display layout for TimelineItem.
 * Single-column, top-down information flow optimized for reading speed.
 */
export const TimelineItemContentV2: React.FC<ContentV2Props> = ({
  date,
  title,
  doctorName,
  content,
  sourceText,
  pdfFileName,
  rowNumber,
  entryInfo,
  isExpanded,
  searchQuery,
  activeMatchIndex = -1,
  validationIssues,
  citations,
  onOpenCitation,
  onRemoveCitation,
  onFileTicketClick,
  onViewVideoClick,
}) => {
  // Markers need both handlers to be interactive; without them the injected
  // `<sup>` would render empty and leak its ids into the DOM, so skip entirely.
  const citationsRenderable =
    (citations?.length ?? 0) > 0 && !!onOpenCitation && !!onRemoveCitation;
  // Display numbers are a pure rendering concern, never stored — derived from
  // where the anchors land so they read 1,2,3 down the entry.
  const { content: citedContent, numbers: citationNumbers } = useMemo(
    () =>
      citationsRenderable
        ? injectCitationMarkers(content, citations ?? [])
        : { content, numbers: new Map<string, number>() },
    [citationsRenderable, content, citations],
  );

  // Matches are numbered date → title → doctorName → content (visual
  // top-to-bottom), the same order useTimeline walks them in. Each field gets
  // the active index rebased into its own range, or -1 when the active match
  // lives in another field.
  const matchCounts = useMemo(
    () =>
      getItemSearchMatches({ date, title, doctorName, content }, searchQuery),
    [date, title, doctorName, content, searchQuery],
  );
  const titleStart = matchCounts.date;
  const doctorStart = titleStart + matchCounts.title;
  const contentStart = doctorStart + matchCounts.doctorName;
  const rebase = (start: number, count: number) =>
    activeMatchIndex >= start && activeMatchIndex < start + count
      ? activeMatchIndex - start
      : -1;
  const dateActiveIndex = rebase(0, matchCounts.date);
  const titleActiveIndex = rebase(titleStart, matchCounts.title);
  const doctorActiveIndex = rebase(doctorStart, matchCounts.doctorName);
  const contentActiveIndex =
    activeMatchIndex >= contentStart ? activeMatchIndex - contentStart : -1;

  // Both branches feed MarkdownBody the same prop, so the search/no-search
  // split is a string decision rather than two near-identical JSX trees.
  const renderedMarkdown = useMemo(
    () =>
      searchQuery?.trim()
        ? highlightSearchTerm(citedContent, searchQuery, contentActiveIndex)
        : citedContent,
    [citedContent, searchQuery, contentActiveIndex],
  );

  return (
    <div className="indigo-timeline-item__content-v2">
      {/* Row 1: Date + visit badge + important tag */}
      <div className="indigo-timeline-item__content-v2-header">
        <Heading level={2} className="indigo-timeline-item__date">
          {date ? (
            <HighlightedText
              text={formatISODate(date)}
              ranges={matchCounts.dateRanges}
              activeMatchIndex={dateActiveIndex}
            />
          ) : (
            "Unknown"
          )}
        </Heading>
        {entryInfo &&
          (entryInfo.isCurrent ? (
            <Tooltip
              content={`${entryInfo.appointmentTypeLabel} on ${entryInfo.appointmentDate ? formatISODate(entryInfo.appointmentDate) : "unknown date"}`}
              placement="top"
              size="md"
            >
              <span className="indigo-timeline-item__entry-tag">
                <Tag
                  label="Current appointment"
                  variant="filled"
                  status="info"
                  shape="pill"
                  size="medium"
                />
              </span>
            </Tooltip>
          ) : (
            <Tooltip
              content={`${entryInfo.appointmentTypeLabel} on ${entryInfo.appointmentDate ? formatISODate(entryInfo.appointmentDate) : "unknown date"}${entryInfo.href ? " — click to open this entry in a new tab" : ""}`}
              placement="top"
              size="md"
            >
              {entryInfo.href ? (
                <a
                  className="indigo-timeline-item__entry-tag indigo-timeline-item__entry-tag--link"
                  href={entryInfo.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tag
                    label={`Other appointment: ${entryInfo.appointmentDate ? formatISODate(entryInfo.appointmentDate) : "unknown"}`}
                    variant="outlined"
                    status="info"
                    shape="pill"
                    size="medium"
                  />
                </a>
              ) : (
                <span className="indigo-timeline-item__entry-tag">
                  <Tag
                    label={`Other appointment: ${entryInfo.appointmentDate ? formatISODate(entryInfo.appointmentDate) : "unknown"}`}
                    variant="outlined"
                    status="info"
                    shape="pill"
                    size="medium"
                  />
                </span>
              )}
            </Tooltip>
          ))}
      </div>

      {/* Row 2 & 3: Record title, doctor name */}
      <div>
        <div className="indigo-timeline-item__content-v2-title-row">
          <Heading level={2}>
            <HighlightedText
              text={title}
              searchQuery={searchQuery}
              activeMatchIndex={titleActiveIndex}
            />
          </Heading>
        </div>

        {/* Row 3: Doctor name — hidden when empty (e.g. sub-rosa entries
            that have no associated provider). */}
        {doctorName && (
          <Body
            size="md"
            className="indigo-timeline-item__doctor text-indigo-text-tertiary"
          >
            <HighlightedText
              text={doctorName}
              searchQuery={searchQuery}
              activeMatchIndex={doctorActiveIndex}
            />
          </Body>
        )}
      </div>

      {/* Inline validation issues */}
      {validationIssues}

      {/* Row 4: Markdown content */}
      {isExpanded && (
        <div className="indigo-timeline-item__description">
          <MarkdownBody
            markdown={renderedMarkdown}
            citationNumbers={citationNumbers}
            onOpenCitation={onOpenCitation}
            onRemoveCitation={onRemoveCitation}
          />
        </div>
      )}

      {/* Row 4.5: View video button — sub-rosa entries only (gated by
          presence of onViewVideoClick on the parent). */}
      {isExpanded && onViewVideoClick && (
        <div className="indigo-timeline-item__content-v2-actions">
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="video" />}
            onClick={(e) => {
              e.stopPropagation();
              onViewVideoClick();
            }}
          >
            View video
          </Button>
        </div>
      )}

      {/* Row 5: Source + row number footer */}
      {isExpanded && (
        <div className="indigo-timeline-item__content-v2-footer">
          {pdfFileName ? (
            <Tooltip content={pdfFileName} placement="top-start">
              <span>
                <Body size="md" className="text-sky-500 font-normal inline">
                  {sourceText}
                </Body>
              </span>
            </Tooltip>
          ) : (
            <Body size="md" className="text-sky-500 font-normal">
              {sourceText}
            </Body>
          )}
          <div className="flex items-center gap-1 ml-auto">
            {onFileTicketClick && (
              <IconButton
                className="indigo-timeline-item__file-ticket-btn"
                iconName="bug"
                variant="ghost"
                size="sm"
                tooltip="Report bug"
                onClick={(e) => {
                  e.stopPropagation();
                  onFileTicketClick();
                }}
              />
            )}
            {rowNumber !== undefined && (
              <Body size="md" className="text-indigo-text-disabled font-normal">
                #{rowNumber}
              </Body>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
